import { ErrorBoundary } from '@libs/templates/template-pages';
import { Route } from 'react-router';
import { ContainerV2, ContainerWithContext, FaContainer } from './layouts';

/**
 * FA の画面。provider 群は {@link FaContainer} で1度だけ mount し、その下に既存UI（v1）の
 * {@link ContainerWithContext} と新UI（v2）の {@link ContainerV2} の枠を兄弟の layout route
 * として置く（枠を入れ子にすると枠と高さ計算が二重になる。使い分けは {@link ContainerV2} 参照）。
 */
export const faRouter = (
  <Route path="/fa" element={<FaContainer />} errorElement={<ErrorBoundary />}>
    <Route element={<ContainerV2 />}>
      <Route path="space" lazy={async () => import('./pages/Space')} />
    </Route>
    <Route element={<ContainerWithContext />}>
      <Route index lazy={async () => import('./pages/MainMenu')} />
      <Route path="main-menu" lazy={async () => import('./pages/MainMenu')} />
      <Route path="accounting-policy/*" lazy={async () => import('./pages/AccountingPolicyIndex')} />
      <Route
        path="article-management-settings"
        lazy={async () => import('./pages/ArticleManagementSettings')}
      />
      <Route
        path="business-connection-mst/*"
        lazy={async () => import('./pages/BusinessConnectionMstIndex')}
      />
      <Route path="carry-over" lazy={async () => import('./pages/CarryOver')} />
      <Route path="category-mst/*" lazy={async () => import('./pages/CategoryMstIndex')} />
      <Route
        path="confirm-settlement-status"
        lazy={async () => import('./pages/ClosingStateCheckRegistration')}
      />
      <Route path="custom-field-setting/*" lazy={async () => import('./pages/CustomFieldSettingIndex')} />
      <Route path="custom-mst/:customFieldKey/*" lazy={async () => import('./pages/CustomMstIndex')} />
      <Route path="fa-custom-field-change/*" lazy={async () => import('./pages/FaCustomFieldChangeIndex')} />
      <Route
        path="fa-custom-field-change-import"
        lazy={async () => import('./pages/FaCustomFieldChangeImport')}
      />
      <Route
        path="fa-depreciation-item-change-import"
        lazy={async () => import('./pages/FaDepreciationItemChangeImport')}
      />
      <Route
        key="fa-impairment-detail-table"
        path="fa-impairment-detail-table"
        lazy={async () => import('./pages/FaImpairmentDetailTable')}
      />
      <Route
        path="fixed-asset-cancel-incre-decre"
        lazy={async () => import('./pages/FixedAssetCancelIncreDecre')}
      />
      <Route
        path="fixed-asset-depreciation-item-change/*"
        lazy={async () => import('./pages/FixedAssetDepreciationItemChange')}
      />
      <Route
        path="fixed-asset-impairment-loss-entry/*"
        lazy={async () => import('./pages/FixedAssetImpairmentLossEntryIndex')}
      />
      <Route
        path="fixed-asset-impairment-loss-entry-import"
        lazy={async () => import('./pages/FixedAssetImpairmentLossEntryImport')}
      />
      <Route path="lease-incr-decr-table" lazy={async () => import('./pages/LeaseIncrDecrTable')} />
      <Route path="fixed-asset-ledger-details" lazy={async () => import('./pages/FixedAssetLedgerDetails')} />
      <Route path="fixed-asset-list-inquiry" lazy={async () => import('./pages/FixedAssetListInquiry')} />
      <Route path="fixed-asset-retire/*" lazy={async () => import('./pages/FixedAssetRetireIndex')} />
      <Route path="interest-rate-setting/*" lazy={async () => import('./pages/InterestRateSetting')} />
      <Route path="job-watch/*" lazy={async () => import('./pages/JobWatchRegistration')} />
      <Route path="la-article-info-change/*" lazy={async () => import('./pages/LaArticleInfoChangeIndex')} />
      <Route path="la-article-ledger-details" lazy={async () => import('./pages/LaArticleLedgerDetails')} />
      <Route path="la-article-list-search" lazy={async () => import('./pages/LaArticleListSearch')} />
      <Route
        path="la-article-principal-repayment-schedule"
        lazy={async () => import('./pages/LaArticlePrincipalRepaymentSchedule')}
      />
      <Route path="la-cancel-bef-maturity/*" lazy={async () => import('./pages/CancelBefMaturityIndex')} />
      <Route
        path="la-cancel-bef-maturity-import"
        lazy={async () => import('./pages/LaCancelBefMaturityImport')}
      />
      <Route path="la-cancel-registration" lazy={async () => import('./pages/LaCancelRegistrationSearch')} />
      <Route
        path="la-contract-info-change/*"
        lazy={async () => import('./pages/LaContractInfoChangeIndex')}
      />
      <Route
        path="la-contract-info-change-import"
        lazy={async () => import('./pages/LaContractInfoChangeImport')}
      />
      <Route
        path="la-article-info-change-import"
        lazy={async () => import('./pages/LaArticleInfoChangeImport')}
      />
      <Route path="la-contract-ledger-details" lazy={async () => import('./pages/LaContractLedgerDetails')} />
      <Route
        path="la-contract-payment-confirmation-form"
        lazy={async () => import('./pages/LaContractPaymentConfirmationFrom')}
      />
      <Route path="la-migrate-balance-delete" lazy={async () => import('./pages/LaMigrateBalanceDelete')} />
      <Route path="la-migrate-balance-import" lazy={async () => import('./pages/LaMigrateBalanceImport')} />
      <Route
        path="la-migrate-balance-individual-delete"
        lazy={async () => import('./pages/LaMigrateBalanceIndividualDelete')}
      />
      <Route path="la-start/*" lazy={async () => import('./pages/LaStartRegistrationIndex')} />
      <Route path="la-start-import" lazy={async () => import('./pages/LaStartImport')} />
      <Route path="lease-company-mst/*" lazy={async () => import('./pages/LeaseCompanyMstIndex')} />
      <Route
        path="lease-contract-list-reference"
        lazy={async () => import('./pages/LeaseContractListReference')}
      />
      <Route
        path="lease-estimate-change-import"
        lazy={async () => import('./pages/LeaseEstimateChangeImport')}
      />
      <Route
        path="lease-expense-summary-table"
        lazy={async () => import('./pages/LeaseExpenseSummaryTable')}
      />
      <Route
        path="lease-liability-estimate-change/*"
        lazy={async () => import('./pages/LeaseLiabilityEstimateChangeIndex')}
      />
      <Route
        path="lease-liability-estimate-change-list"
        lazy={async () => import('./pages/LeaseLiabilityEstimateChangeList')}
      />
      <Route path="lease-liability-ledger" lazy={async () => import('./pages/LeaseLiabilityLedger')} />
      <Route path="location-mst/*" lazy={async () => import('./pages/LocationMstIndex')} />
      <Route path="mst-setting" lazy={async () => import('./pages/MstInfoSettings')} />
      <Route path="operation-control" lazy={async () => import('./pages/OperationControl')} />
      <Route
        path="re-lease-and-contract-expired/*"
        lazy={async () => import('./pages/ReLeaseAndContractExpiredIndex')}
      />
      <Route
        path="re-lease-and-contract-expired-import"
        lazy={async () => import('./pages/ReLeaseAndContractExpiredImport')}
      />
      <Route path="segment-mst/:segmentMstKey/*" lazy={async () => import('./pages/SegmentMstIndex')} />
      <Route path="sub-category-mst/*" lazy={async () => import('./pages/SubCategoryMstIndex')} />
      <Route path="*" lazy={async () => import('@libs/templates/template-pages/NotFound')} />
    </Route>
  </Route>
);
